import React, { useState, useRef, useEffect } from "react";
import { useForm } from "react-hook-form";
import {
  ChevronLeft,
  ChevronRight,
  FileText,
  Building2,
  Upload,
  CheckCircle,
  DollarSign,
  Shield,
  ArrowLeft,
  AlertCircle,
  AlertTriangle,
  X,
} from "lucide-react";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { bondsAPI, walletAPI, filesAPI } from "../../services/api";
import { useNavigate } from "react-router-dom";
import { toast } from 'sonner';
import LoadingSpinner from "../../components/common/LoadingSpinner";
import {
  extractResponseData,
  extractErrorMessage,
  extractSuccessMessage,
} from "../../utils/apiHelpers";

// Convert "YYYY-MM-DD" to Malaysia midnight in UTC
const toMalaysiaMidnightUTC = (dateString) => {
  if (!dateString) return null;
  // Append Malaysia timezone offset (+08:00) to treat the date as Malaysia midnight
  // Then convert to ISO string (UTC)
  return new Date(dateString + 'T00:00:00+08:00').toISOString();
};

const CreateBond = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [bondId, setBondId] = useState(null);
  const [selectedBondType, setSelectedBondType] = useState("");
  const [uploadedFiles, setUploadedFiles] = useState({});
  const [uploadedFileStatuses, setUploadedFileStatuses] = useState({});
  const [uploadingDocuments, setUploadingDocuments] = useState(false);
  const [documentUploadProgress, setDocumentUploadProgress] = useState(0); // 0-100

  const [showSubmissionSuccess, setShowSubmissionSuccess] = useState(false);
  const [formData, setFormData] = useState({});
  const [showTokenLoading, setShowTokenLoading] = useState(false); // legacy (token details save)
  const [isTokenCreationInProgress, setIsTokenCreationInProgress] = useState(false);
  const tokenPollRef = useRef(null);

  const uploadDocumentsForBond = async (targetBondId, categories = null) => {
    const selectedCategories = categories ? new Set(categories) : null;
    const skippedCategories = [];
    const filesToUpload = [];

    Object.entries(uploadedFiles).forEach(([category, value]) => {
      if (!value) return;
      if (selectedCategories && !selectedCategories.has(category)) return;

      const alreadyUploaded = uploadedFileStatuses[category];
      if (alreadyUploaded) {
        skippedCategories.push(category);
        return;
      }

      const valuesArray = Array.isArray(value) ? value : [value];
      valuesArray.forEach((file) => {
        if (file) {
          filesToUpload.push({ category, file });
        }
      });
    });

    if (filesToUpload.length === 0) {
      setDocumentUploadProgress(0);
      return {
        uploadedCount: 0,
        processedCount: 0,
        totalFiles: 0,
        errors: [],
        skippedCategories,
      };
    }

    setUploadingDocuments(true);
    setDocumentUploadProgress(0);

    let processedCount = 0;
    let uploadedCount = 0;
    const errors = [];

    for (const { category, file } of filesToUpload) {
      const formDataPayload = new FormData();
      formDataPayload.append("file", file);
      formDataPayload.append("type", "document");
      formDataPayload.append(
        "relatedTo",
        JSON.stringify({ module: "bonds", id: targetBondId })
      );
      formDataPayload.append(
        "metadata",
        JSON.stringify({ category, originalName: file.name })
      );

      try {
        await filesAPI.uploadFile(formDataPayload);
        uploadedCount += 1;
        setUploadedFileStatuses((prev) => ({
          ...prev,
          [category]: true,
        }));
      } catch (err) {
        console.error(`Failed to upload file ${file.name}`, err);
        errors.push(file.name);
        setUploadedFileStatuses((prev) => ({
          ...prev,
          [category]: false,
        }));
      } finally {
        processedCount += 1;
        setDocumentUploadProgress(
          Math.round((processedCount / filesToUpload.length) * 100)
        );
      }
    }

    setDocumentUploadProgress(100);
    setUploadingDocuments(false);

    return {
      uploadedCount,
      processedCount,
      totalFiles: filesToUpload.length,
      errors,
      skippedCategories,
    };
  };

  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
  } = useForm({
    defaultValues: {
      isESGBond: false,
      currency: 'MYR',
      issuePrice: 100,
      minimumInvestmentValue: 1000,
    }
  });

  // Local state to stabilize ESG selection and avoid transient watch resets
  const [isESGBondLocal, setIsESGBondLocal] = useState(false);
  const hasRegisteredESGRef = useRef(false);
  useEffect(() => {
    if (!hasRegisteredESGRef.current) {
      // Register without required rule; default false is accepted
      register('isESGBond');
      hasRegisteredESGRef.current = true;
    }
  }, [register]);
  // Sync local state into RHF form value
  useEffect(() => {
    setValue('isESGBond', isESGBondLocal, { shouldValidate: true });
  }, [isESGBondLocal, setValue]);
  // Fallback coercion if state becomes non-boolean
  useEffect(() => {
    if (isESGBondLocal !== true && isESGBondLocal !== false) {
      setIsESGBondLocal(false);
    }
  }, [isESGBondLocal]);

  // Fetch user's wallet
  const {
    data: walletData,
    isLoading: walletLoading,
    error: walletError,
  } = useQuery({
    queryKey: ["myWallet"],
    queryFn: walletAPI.getMyWallet,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Fetch current bond data if bondId exists
  const { data: bondData } = useQuery({
    queryKey: ["bond", bondId],
    queryFn: () => bondsAPI.getBondById(bondId),
    enabled: !!bondId,
    staleTime: 30 * 1000, // 30 seconds
  });

  const steps = [
    {
      id: 1,
      title: "Bond Type",
      icon: Building2,
      description: "Select bond type",
    },
    {
      id: 2,
      title: "Basic Information",
      icon: FileText,
      description: "Enter bond details",
    },
    {
      id: 3,
      title: "Bond Details",
      icon: Shield,
      description: "Specific requirements",
    },
    {
      id: 4,
      title: "Token Information",
      icon: DollarSign,
      description: "Enter token details",
    },
    {
      id: 5,
      title: "Review & Submit",
      icon: CheckCircle,
      description: "Final review",
    },
  ];

  // Create bond mutation
  const createBondMutation = useMutation({
    mutationFn: bondsAPI.createBond,
    onSuccess: async (response) => {
      console.log("Create bond response:", response);
      const data = extractResponseData(response);

      if (data && data.bond && data.bond._id) {
        const newBondId = data.bond._id;
        setBondId(newBondId);
        toast.success(
          extractSuccessMessage(response, "Bond created successfully")
        );

        try {
          const uploadResult = await uploadDocumentsForBond(newBondId);

          if (uploadResult.errors.length > 0) {
            toast.error(
              `Some documents failed to upload: ${uploadResult.errors.join(
                ", "
              )}. You can retry from the Review step.`,
              { duration: 6000 }
            );
          } else if (uploadResult.uploadedCount > 0) {
            toast.success("All documents uploaded successfully");
          }

          setCurrentStep((prev) => prev + 1);
        } catch (e) {
          console.error("Unexpected error during document upload", e);
          toast.error(
            extractErrorMessage(
              e,
              "Unexpected error while uploading documents. Please try again."
            )
          );
        }
      } else {
        console.error("Invalid response structure:", data);
        toast.error("Failed to create bond - invalid response");
      }
    },
    onError: (error) => {
      console.error("Create bond error:", error);
      toast.error(extractErrorMessage(error, "Failed to create bond"));
    },
  });

  // Update bond mutation
  const updateBondMutation = useMutation({
    mutationFn: ({ id, data }) => bondsAPI.updateBond(id, data),
    onSuccess: (response) => {
      // Refetch bond data to get latest token creation status
      queryClient.invalidateQueries(["bond", bondId]);

      if (currentStep < 6) {
        setCurrentStep(currentStep + 1);
      }
      toast.success(
        extractSuccessMessage(response, "Bond updated successfully")
      );
    },
    onError: (error) => {
      toast.error(extractErrorMessage(error, "Failed to update bond"));
    },
  });

  // Submit bond mutation
  const submitBondMutation = useMutation({
    mutationFn: bondsAPI.submitBond,
    onSuccess: async (response) => {
      queryClient.invalidateQueries(["bonds"]);
      queryClient.invalidateQueries(["bond", bondId]);

      const successMessage = extractSuccessMessage(
        response,
        "Bond submitted successfully! Pending review."
      );
      toast.success(successMessage, { duration: 4000 });

      // Determine if on-chain token creation is expected (token details exist & not already created)
      const formValues = watch();
      const tokenDetailsEntered = formValues.tokenName && formValues.tokenSymbol && (formValues.tokenDecimals !== undefined) && formValues.faceValuePerToken && formValues.tokenWallet;

      // If no token creation expected, show normal success screen immediately
      if (!tokenDetailsEntered) {
        setShowSubmissionSuccess(true);
        return;
      }

      // Start overlay for token creation progress
      setIsTokenCreationInProgress(true);
      setShowSubmissionSuccess(true); // show success screen beneath overlay

      tokenPollRef.current = setInterval(async () => {
        try {
          const latest = await bondsAPI.getBondById(bondId);
          const latestBond = latest?.data?.bond || latest?.data?.data?.bond;
          const td = latestBond?.tokenDetails;
          if (td?.tokenAddress && td?.tokenCreationStatus === 'created') {
            clearInterval(tokenPollRef.current);
            tokenPollRef.current = null;
            setIsTokenCreationInProgress(false);
            toast.success('On-chain token created successfully.', { duration: 4000 });
          } else if (td?.tokenCreationStatus === 'failed') {
            clearInterval(tokenPollRef.current);
            tokenPollRef.current = null;
            setIsTokenCreationInProgress(false);
            toast.error('Token creation failed. Adjust details and resubmit.', { duration: 6000 });
          }
        } catch (e) {
          // Silent retry
        }
      }, 4000);
    },
    onError: (error) => {
      const errorMessage = extractErrorMessage(
        error,
        "Failed to submit bond. Please try again."
      );
      toast.error(errorMessage, { duration: 5000 });
    },
  });

  const handleBondTypeSelection = (bondType) => {
    setSelectedBondType(bondType);

    // Map frontend values to backend expected values
    const bondTypeMap = {
      fixed_notes: "Fixed Notes",
      sukuk: "Sukuk",
    };

    const apiBondType = bondTypeMap[bondType] || bondType;

    // Store bond type in form data and move to next step
    setFormData((prev) => ({
      ...prev,
      bondType: apiBondType,
    }));

    setCurrentStep(2);
  };

  const handleNext = async (data) => {
    if (currentStep === 1) {
      return; // Handled by bond type selection
    }



    // Store form data from current step
    const updatedFormData = {
      ...formData,
      ...data,
      step: Math.min(currentStep, 5),
    };
    setFormData(updatedFormData);
    // STEP 3 SPECIAL HANDLING
    // Requirement: At step 3 the Next button should create the bond ONLY if it hasn't been created yet.
    // If bond already exists, simply proceed to the next step without calling update API here.
    if (currentStep === 3) {
      if (!bondId) {
        // Debug: Log the raw form data
        console.log("CreateBond - Raw form data:", updatedFormData);
        console.log("CreateBond - Bond type:", selectedBondType);

        // Organize data based on bond type for proper backend structure
        const organizedData = { ...updatedFormData };

        if (selectedBondType === "sukuk") {
          // Debug: Log sukuk-specific fields
          console.log("CreateBond - Sukuk fields:", {
            sukukStructureType: data.sukukStructureType,
            sukukAssetType: data.sukukAssetType,
            islamicFinancingRating: data.islamicFinancingRating,
          });

          // Organize Sukuk-specific fields under sukukDetails
          organizedData.sukukDetails = {
            sukukType: data.sukukStructureType,
            islamicFinancialRating: data.islamicFinancingRating,
            underlyingAsset: data.assetDescription,
            sukukStructure: data.sukukStructureType,
            shariahAdvisor: data.shariahBoardName,
            profitSharingRatio: data.profitSharingRatio,
            expectedReturn: data.expectedReturn,
            shariahCompliance: data.shariahCompliance,
            // Additional fields from the form
            sukukAssetType: data.sukukAssetType,
            assetLocation: data.assetLocation,
            assetOwnershipStructure: data.assetOwnershipStructure,
            fatwaReference: data.fatwaReference,
          };

          // Debug: Log organized sukuk details
          console.log(
            "CreateBond - Organized sukukDetails:",
            organizedData.sukukDetails
          );

          // Map profitRate to couponRate for consistency
          organizedData.couponRate = data.profitRate || data.couponRate;

          // Remove flat sukuk fields
          delete organizedData.sukukStructureType;
          delete organizedData.islamicFinancingRating;
          delete organizedData.profitRate;
          delete organizedData.assetDescription;
          delete organizedData.shariahBoardName;
          delete organizedData.profitSharingRatio;
          delete organizedData.expectedReturn;
          delete organizedData.shariahCompliance;
          delete organizedData.sukukAssetType;
          delete organizedData.assetLocation;
          delete organizedData.assetOwnershipStructure;
          delete organizedData.fatwaReference;
        } else if (selectedBondType === "fixed_notes") {
          // Organize Fixed Notes-specific fields under fixedNotesDetails
          organizedData.fixedNotesDetails = {
            creditRating: data.creditRating,
            guarantor: data.guarantor,
            collateral: data.collateral,
            useOfProceeds: data.useOfProceeds,
            riskFactors: data.riskFactors,
            taxImplications: data.taxImplications,
          };

          // Remove flat fixed notes fields
          delete organizedData.creditRating;
          delete organizedData.guarantor;
          delete organizedData.collateral;
          delete organizedData.useOfProceeds;
          delete organizedData.riskFactors;
          delete organizedData.taxImplications;

        }

        // Convert ESG bond string to boolean for database storage
        if (organizedData.isESGBond !== undefined) {
          organizedData.isESGBond = organizedData.isESGBond === true || organizedData.isESGBond === "true";
        }

        // Add productType field required by backend API
        organizedData.productType = organizedData.bondType;

        // Note: Dates are sent as YYYY-MM-DD format to backend
        // Backend will convert Malaysia timezone midnight to UTC

        // Debug: Log final organized data being sent to API
        console.log("CreateBond - Final organized data:", organizedData);

        // Create bond with properly organized data
        createBondMutation.mutate(organizedData);
      } else {
        // Bond already exists -> just move to next step (don't update here per new requirement)
        setCurrentStep(currentStep + 1);
      }
      return; // Prevent falling through to generic update path
    }

    // Generic update path for steps after bond is created (e.g., token info at step 4)
    if (bondId) {
      // Token creation deferred until submission; step 4 now only saves details.
      const isTokenCreationStep = currentStep === 4;

      // Prepare update data
      const updateData = { ...data, step: Math.min(currentStep, 5) };

      // Convert ESG bond string to boolean for database storage
      if (updateData.isESGBond !== undefined) {
        updateData.isESGBond = updateData.isESGBond === true || updateData.isESGBond === "true";
      }

      // Note: Dates are sent as YYYY-MM-DD format to backend
      // Backend will convert Malaysia timezone midnight to UTC

      updateBondMutation.mutate(
        {
          id: bondId,
          data: updateData,
        },
        {
          onSettled: () => {
            // No token creation spinner handling needed (deferred to submission)
          },
        }
      );
    } else {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleFinalSubmit = () => {
    // Always call actual API to save to database
    if (bondId) {
      submitBondMutation.mutate(bondId);
    } else {
      toast.error("Bond ID not found. Please try again.");
    }
  };

  const renderSubmissionSuccess = () => (
    <div className="max-w-2xl mx-auto">
      <div className="text-center">
        <div className="flex items-center justify-center w-16 h-16 mx-auto mb-6 bg-green-100 rounded-full">
          <CheckCircle className="w-8 h-8 text-green-600" />
        </div>
        <h2 className="mb-4 text-2xl font-bold text-gray-900">
          Bond Application Submitted Successfully!
        </h2>
        <p className="mb-6 text-lg text-gray-600">
          Your bond application has been submitted for review and is now being
          reviewed by the Facility Agent team.
        </p>

        <div className="p-6 mb-8 border border-blue-200 rounded-lg bg-blue-50">
          <div className="flex items-center justify-center mb-4">
            <div className="flex items-center space-x-2">
              <span className="relative flex w-3 h-3">
                <span className="absolute inline-flex w-3 h-3 bg-blue-400 rounded-full opacity-75 animate-ping"></span>
                <span className="relative inline-flex w-3 h-3 bg-blue-500 rounded-full"></span>
              </span>
              <span className="font-medium text-blue-800">
                Status: Pending Review
              </span>
            </div>
          </div>
          <p className="text-sm text-blue-700">
            You will receive an email notification once the review is complete.
            You can track the status of your application in the Bond Management
            section.
          </p>
        </div>

        <div className="p-6 mb-8 rounded-lg bg-gray-50">
          {/* <h3 className="mb-4 text-lg font-medium text-gray-900">Next Steps:</h3>
          <div className="space-y-3 text-left">
            <div className="flex items-start">
              <span className="mr-3 text-blue-600">1.</span>
              <span className="text-gray-700">Arranger will review your application and supporting documents</span>
            </div>
            <div className="flex items-start">
              <span className="mr-3 text-blue-600">2.</span>
              <span className="text-gray-700">If approved, your bond will proceed to Exchange for listing</span>
            </div>
            <div className="flex items-start">
              <span className="mr-3 text-blue-600">3.</span>
              <span className="text-gray-700">You will be notified of any additional requirements or final approval</span>
            </div>
          </div> */}
        </div>

        <div className="flex flex-col justify-center gap-4 sm:flex-row">
          <button
            onClick={() => navigate("/issuer/bonds")}
            className="inline-flex items-center px-6 py-3 text-base font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <FileText className="w-5 h-5 mr-2" />
            View All Bonds
          </button>
          <button
            onClick={() => navigate("/issuer")}
            className="inline-flex items-center px-6 py-3 text-base font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    </div>
  );

  const handleFileUpload = (event, fieldName) => {
    const { files, multiple } = event.target;
    if (!files || files.length === 0) return;

    // For legalDocuments (multi) or any input with multiple attribute, store as array
    if (multiple || fieldName === "legalDocuments") {
      const fileArray = Array.from(files);
      setUploadedFiles((prev) => ({
        ...prev,
        [fieldName]: fileArray,
      }));
    } else {
      const file = files[0];
      setUploadedFiles((prev) => ({
        ...prev,
        [fieldName]: file,
      }));
    }
    setUploadedFileStatuses((prev) => ({
      ...prev,
      [fieldName]: false,
    }));
  };

  const handleFileDelete = (fieldName) => {
    setUploadedFiles((prev) => {
      const updated = { ...prev };
      delete updated[fieldName];
      return updated;
    });
    setUploadedFileStatuses((prev) => {
      const updated = { ...prev };
      delete updated[fieldName];
      return updated;
    });

    // Reset the file input
    const inputElement = document.getElementById(getInputId(fieldName));
    if (inputElement) {
      inputElement.value = "";
    }
  };

  const getInputId = (fieldName) => {
    const idMap = {
      riskAssessment: "risk-assessment",
      legalDocuments: "legal-documents",
      shariahCertificate: "shariah-certificate",
      valuationReport: "valuation-report",
      securitiesLodgementForm: "securities-lodgement-form",
      globalCertificate: "global-certificate",
      debtFacilityForm: "debt-facility-form",
    };
    return idMap[fieldName] || fieldName;
  };

  const renderStepIndicator = () => (
    <div className="flex items-center justify-center mb-8">
      {steps.map((step, index) => (
        <div key={step.id} className="flex items-center">
          <div
            className={`flex flex-col items-center ${index > 0 ? "ml-8" : ""}`}
          >
            <div
              className={`
              w-12 h-12 rounded-full flex items-center justify-center border-2
              ${currentStep > step.id
                  ? "bg-primary-500 border-primary-500 text-white cursor-pointer hover:bg-primary-600"
                  : currentStep === step.id
                    ? "bg-white border-primary text-primary"
                    : "bg-white border-gray-400 text-gray-400"
                }
            `}
              onClick={() => {
                // Allow navigation to previous steps or current step
                if (step.id <= currentStep) {
                  setCurrentStep(step.id);
                }
              }}
            >
              <step.icon className="w-5 h-5" />
            </div>
            <div className="mt-2 text-center">
              <div className="text-sm font-medium text-secondary-900">
                {step.title}
              </div>
              <div className="text-xs text-secondary-500">
                {step.description}
              </div>
            </div>
          </div>
          {index < steps.length - 1 && (
            <div
              className={`
              w-16 h-0.5 mt-6 ml-4
              ${currentStep > step.id ? "bg-primary-500" : "bg-gray-400"}
            `}
            />
          )}
        </div>
      ))}
    </div>
  );

  const renderStep1 = () => {
    return (
      <div className="mx-auto max-w-7xl">
        <div className="card">
          <div className="card-body">
            <div className="mb-8 text-center">
              <h2 className="mb-2 text-2xl font-bold ">
                Select Bond Type
              </h2>
            </div>

            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              {/* Conventional Card */}
              <div
                className="flex flex-col items-center justify-center w-full p-8 text-sm font-medium border rounded cursor-pointer btn-primary-hover focus:outline-none"
                onClick={() => handleBondTypeSelection("fixed_notes")}
              >
                <div className="text-center">
                  <DollarSign
                    className="w-16 h-16 mx-auto mb-4"
                    style={{ color: "var(--color-primary)" }}
                  />
                  <h3
                    className="mb-4 text-2xl font-bold"
                    style={{ color: "var(--color-primary)" }}
                  >
                    Conventional
                  </h3>
                </div>
                <p className="mb-6 text-secondary-600">
                  Create an asset-backed security compliant digital bond.
                </p>
              </div>

              {/* Sukuk Card */}
              <div
                className="flex flex-col items-center justify-center w-full p-8 text-sm font-medium border rounded cursor-pointer btn-primary-hover focus:outline-none"
                onClick={() => handleBondTypeSelection("sukuk")}
              >
                <div className="text-center">
                  <Shield
                    className="w-16 h-16 mx-auto mb-4"
                    style={{ color: "var(--color-primary)" }}
                  />
                  <h3
                    className="mb-4 text-2xl font-bold"
                    style={{ color: "var(--color-primary)" }}
                  >
                    Sukuk(Islamic Bond)
                  </h3>
                  <p className="mb-6 text-secondary-600">
                    Create an asset-backed security compliant with Shariah
                    principles for Islamic finance.
                  </p>
                  <div className="space-y-2 text-sm text-secondary-500">
                    {/* <div className="flex items-center justify-center">
                     <CheckCircle className="w-4 h-4 mr-2 text-success-500" />
                     <span>Shariah compliant</span>
                   </div>
                   <div className="flex items-center justify-center">
                     <CheckCircle className="w-4 h-4 mr-2 text-success-500" />
                     <span>Asset-backed</span>
                   </div>
                   <div className="flex items-center justify-center">
                     <CheckCircle className="w-4 h-4 mr-2 text-success-500" />
                     <span>Profit sharing</span>
                   </div> */}
                  </div>
                </div>
              </div>
            </div>

            {createBondMutation.isPending && (
              <div className="flex justify-center mt-8">
                <LoadingSpinner />
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderStep2 = () => (
    <div className="max-w-4xl mx-auto">
      <div className="card">
        <div className="card-header">
          <h2 className="text-lg font-medium text-secondary-900">
            Basic Bond Information
          </h2>
          <p className="mt-1 text-sm text-secondary-600">
            Enter the core details for your{" "}
            {selectedBondType === "fixed_notes" ? "Conventional" : "Sukuk"}
          </p>
        </div>
        <div className="card-body">
          <form onSubmit={handleSubmit(handleNext)} className="space-y-6">
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              {/* Bond Name */}
              <div className="md:col-span-2">
                <label className="block mb-2 text-sm font-medium text-dark-gray">
                  Bond Name <span className="required-asterisk">*</span>
                </label>
                <input
                  {...register("bondName", {
                    required: "Bond name is required",
                    minLength: {
                      value: 3,
                      message: "Bond name must be at least 3 characters",
                    },
                  })}
                  className="w-full px-3 py-2 border rounded-md border-light-gray focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="e.g., Khazanah Conventional Bond 2026"
                />
                {errors.bondName && (
                  <p className="mt-1 text-sm text-error-600">
                    {errors.bondName.message}
                  </p>
                )}
              </div>

              {/* Location Code */}
              <div>
                <label className="block mb-2 text-sm font-medium text-dark-green">
                  Location Code <span className="required-asterisk">*</span>
                </label>
                <input
                  type="text"
                  {...register("locationCode", {
                    required: "Location code is required",
                    minLength: {
                      value: 2,
                      message: "Location code must have at least 2 characters",
                    },
                    maxLength: {
                      value: 10,
                      message: "Location code must be 10 characters or fewer",
                    },
                  })}
                  className="w-full px-3 py-2 border rounded-md border-light-gray focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="e.g., MLY"
                  style={{ textTransform: "uppercase" }}
                  onInput={(e) =>
                    (e.target.value = e.target.value.toUpperCase())
                  }
                />
                {errors.locationCode && (
                  <p className="mt-1 text-sm text-error-600">
                    {errors.locationCode.message}
                  </p>
                )}
                <p className="mt-1 text-xs text-secondary-500">
                  Regulatory location code used for reporting and settlement.
                </p>
              </div>

              {/* Scripless Flag */}
              <div>
                <label className="block mb-2 text-sm font-medium text-dark-green">
                  Scripless Issuance{" "}
                  <span className="required-asterisk">*</span>
                </label>
                <select
                  {...register("scriplessFlag", {
                    // We need to allow both boolean true and false; using `required` would fail when the value is false.
                    // So we convert the string to a boolean (or undefined if not chosen) and then validate that it's not undefined.
                    setValueAs: (value) => {
                      if (value === "") return undefined; // user hasn't selected yet
                      return value === "true"; // returns true or false (for "false")
                    },
                    validate: (value) =>
                      value !== undefined ||
                      "Please specify if the bond is scripless",
                  })}
                  className="w-full px-3 py-2 border rounded-md border-light-gray focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                  defaultValue=""
                >
                  <option value="" disabled>
                    Select option
                  </option>
                  <option value="true">Yes, the bond will be scripless</option>
                  <option value="false">
                    No, physical certificates will be issued
                  </option>
                </select>
                {errors.scriplessFlag && (
                  <p className="mt-1 text-sm text-error-600">
                    {errors.scriplessFlag.message}
                  </p>
                )}
                <p className="mt-1 text-xs text-secondary-500">
                  Scripless bonds are recorded electronically without physical
                  certificates.
                </p>
              </div>

              {/* ESG Bond Classification (controlled) */}
              <div className="space-y-4 md:col-span-2">
                <div>
                  <label className="block mb-2 text-sm font-medium text-dark-gray">
                    ESG/Sustainability Bond <span className="required-asterisk">*</span>
                  </label>
                  <div className="flex items-center space-x-4">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="isESGBond"
                        value="true"
                        checked={isESGBondLocal === true}
                        onChange={() => setIsESGBondLocal(true)}
                        className="w-4 h-4 text-primary-600 focus:ring-primary-500 border-light-gray"
                      />
                      <span className="ml-2 text-sm text-dark-gray">Yes</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="isESGBond"
                        value="false"
                        checked={isESGBondLocal === false}
                        onChange={() => setIsESGBondLocal(false)}
                        className="w-4 h-4 text-primary-600 focus:ring-primary-500 border-light-gray"
                      />
                      <span className="ml-2 text-sm text-dark-gray">No</span>
                    </label>
                  </div>
                </div>

                {/* ESG Certification Upload - always visible and optional */}
                <div className="mt-2">
                  <label className="block mb-2 text-sm font-medium text-dark-gray">
                    Upload ESG Certification (PDF, DOC, PNG, JPG) <span className="text-xs font-normal text-secondary-500">(Optional)</span>
                  </label>
                  <div className="flex items-center space-x-3">
                    {/* Hidden native file input to prevent duplicate filename rendering */}
                    <input
                      id={getInputId("esgCertification")}
                      type="file"
                      accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
                      onChange={(e) => handleFileUpload(e, "esgCertification")}
                      className="hidden"
                    />
                    <label
                      htmlFor={getInputId("esgCertification")}
                      className="px-4 py-2 text-sm font-semibold border rounded-md cursor-pointer bg-primary-50 text-primary-700 hover:bg-primary-100 border-primary-200"
                    >
                      Browse...
                    </label>
                    {uploadedFiles?.esgCertification && (
                      <div className="flex items-center space-x-2 text-sm">
                        <span className="max-w-xs font-medium text-green-700 truncate">
                          {uploadedFiles.esgCertification.name}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleFileDelete("esgCertification")}
                          className="underline text-error-600 hover:text-error-700"
                        >
                          Remove
                        </button>
                      </div>
                    )}
                  </div>
                  <p className="mt-1 text-xs text-secondary-500">
                    You may upload evidence of ESG/Sustainability certification to support classification.
                  </p>
                </div>
              </div>

              {/* Issue Date */}
              <div>
                <label className="block mb-2 text-sm font-medium text-dark-green">
                  Issue Date <span className="required-asterisk">*</span>
                </label>
                <input
                  type="date"
                  {...register("issueDate", {
                    required: "Issue date is required",
                  })}
                  onChange={(e) => {
                    setValue("issueDate", e.target.value, { shouldValidate: true });
                    const maturityDate = watch("maturityDate");
                    if (maturityDate) {
                      setValue("maturityDate", maturityDate, { shouldValidate: true });
                    }
                  }}
                  min={new Date().toISOString().split("T")[0]}
                  className="w-full px-3 py-2 border rounded-md border-light-gray focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                />
                {errors.issueDate && (
                  <p className="mt-1 text-sm text-error-600">
                    {errors.issueDate.message}
                  </p>
                )}
              </div>

              {/* Maturity Date */}
              <div>
                <label className="block mb-2 text-sm font-medium text-dark-green">
                  Maturity Date <span className="required-asterisk">*</span>
                </label>
                <input
                  type="date"
                  {...register("maturityDate", {
                    required: "Maturity date is required",
                    validate: (value) => {
                      const issueDate = watch("issueDate");
                      if (issueDate && value) {
                        const issueDateObj = new Date(issueDate);
                        const maturityDateObj = new Date(value);
                        if (maturityDateObj <= issueDateObj) {
                          return "Maturity date must be after the issue date";
                        }
                      }
                      return true;
                    }
                  })}
                  onChange={(e) => {
                    setValue("maturityDate", e.target.value, { shouldValidate: true });
                  }}
                  min={new Date().toISOString().split("T")[0]}
                  className="w-full px-3 py-2 border rounded-md border-light-gray focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                />
                {errors.maturityDate && (
                  <p className="mt-1 text-sm text-error-600">
                    {errors.maturityDate.message}
                  </p>
                )}
              </div>

              {/* Issue Amount */}
              <div>
                <label className="block mb-2 text-sm font-medium text-dark-green">
                  Issue Amount
                  <span className="required-asterisk">*</span>
                </label>
                <input
                  type="number"
                  step="1"
                  onKeyDown={(e) => {
                    if (e.key === '.' || e.key === 'e' || e.key === 'E' || e.key === '+' || e.key === '-') {
                      e.preventDefault();
                    }
                  }}
                  onPaste={(e) => {
                    const pastedData = e.clipboardData.getData('text');
                    if (!/^\d+$/.test(pastedData)) {
                      e.preventDefault();
                    }
                  }}
                  {...register("issueAmount", {
                    required: "Issue amount is required",
                    min: {
                      value: 1000,
                      message: "Minimum amount is MYR 1,000",
                    },
                    validate: {
                      isInteger: (value) => Number.isInteger(Number(value)) || "Issue amount must be a whole number",
                    },
                  })}
                  className="w-full px-3 py-2 border rounded-md border-light-gray focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="1000000"
                />
                {errors.issueAmount && (
                  <p className="mt-1 text-sm text-error-600">
                    {errors.issueAmount.message}
                  </p>
                )}
              </div>

              {/* Issue Price */}
              <div>
                <label className="block mb-2 text-sm font-medium text-dark-green">
                  Issue Price (%) <span className="required-asterisk">*</span>
                </label>
                <input
                  type="number"
                  step="1"
                  onKeyDown={(e) => {
                    if (e.key === '.' || e.key === 'e' || e.key === 'E' || e.key === '+' || e.key === '-') {
                      e.preventDefault();
                    }
                  }}
                  {...register("issuePrice", {
                    required: "Issue price is required",
                    min: { value: 50, message: "Minimum price is 50%" },
                    max: { value: 150, message: "Maximum price is 150%" },
                    setValueAs: (value) => {
                      if (value === "" || value === null || value === undefined) return "";
                      return parseInt(value, 10);
                    },
                    validate: (value) =>
                      Number.isInteger(value) || "Issue price must be an integer",
                  })}
                  className="w-full px-3 py-2 border rounded-md border-light-gray focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="100"
                  defaultValue="100"
                  onInput={(e) => {
                    // Strip any decimals to enforce integer-only input
                    const val = e.target.value;
                    if (val && val.includes(".")) {
                      e.target.value = val.split(".")[0];
                    }
                  }}
                />
                {errors.issuePrice && (
                  <p className="mt-1 text-sm text-error-600">
                    {errors.issuePrice.message}
                  </p>
                )}
              </div>

              {/* Currency */}
              <div>
                <label className="block mb-2 text-sm font-medium text-dark-green">
                  Currency <span className="required-asterisk">*</span>
                </label>
                <select
                  {...register("currency", {
                    required: "Currency is required",
                  })}
                  className="w-full px-3 py-2 border rounded-md border-light-gray focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                  defaultValue="MYR"
                >
                  <option value="MYR">Malaysian Ringgit (MYR)</option>
                </select>
                {errors.currency && (
                  <p className="mt-1 text-sm text-error-600">
                    {errors.currency.message}
                  </p>
                )}
              </div>

              {/* Minimum Investment Value */}
              <div>
                <label className="block mb-2 text-sm font-medium text-dark-green">
                  Minimum Investment Unit (MYR){" "}
                  <span className="required-asterisk">*</span>
                </label>
                <input
                  type="number"
                  onKeyDown={(e) => {
                    if (e.key === 'e' || e.key === 'E') {
                      e.preventDefault();
                    }
                  }}
                  {...register("minimumInvestmentValue", {
                    required: "Minimum investment unit is required",
                    min: { value: 1000, message: "Minimum unit is MYR 1,000" },
                  })}
                  className="w-full px-3 py-2 border rounded-md border-light-gray focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="1000"
                  defaultValue="1000"
                />
                {errors.minimumInvestmentValue && (
                  <p className="mt-1 text-sm text-error-600">
                    {errors.minimumInvestmentValue.message}
                  </p>
                )}
              </div>
            </div>

            <div className="flex justify-between">
              <button
                type="button"
                onClick={() => setCurrentStep(1)}
                className="flex items-center px-6 py-3 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                style={{
                  fontFamily: '"Roboto", sans-serif',
                  fontWeight: 700,
                  backgroundColor: "var(--color-gray)",
                  color: "var(--color-dark-gray)",
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = "var(--color-medium-gray)";
                  e.target.style.color = "var(--color-dark-gray)";
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = "var(--color-gray)";
                  e.target.style.color = "var(--color-dark-gray)";
                }}
              >
                <ChevronLeft className="w-4 h-4 mr-2" />
                Back
              </button>
              <button
                type="submit"
                disabled={updateBondMutation.isPending || !areBasicFieldsComplete()}
                className="flex items-center px-6 py-3 transition-colors border rounded focus:outline-none focus:ring-2 focus:ring-offset-2"
                style={{
                  fontFamily: '"Roboto", sans-serif',
                  fontWeight: 700,
                  backgroundColor: (updateBondMutation.isPending || !areBasicFieldsComplete()) ? 'var(--color-medium-gray)' : 'var(--color-primary)',
                  borderColor: (updateBondMutation.isPending || !areBasicFieldsComplete()) ? 'var(--color-medium-gray)' : 'var(--color-primary)',
                  color: (updateBondMutation.isPending || !areBasicFieldsComplete()) ? 'var(--color-dark-gray)' : 'white',
                  cursor: (updateBondMutation.isPending || !areBasicFieldsComplete()) ? 'not-allowed' : 'pointer',
                  opacity: (updateBondMutation.isPending || !areBasicFieldsComplete()) ? 0.8 : 1,
                }}
                onMouseEnter={(e) => {
                  if (updateBondMutation.isPending || !areBasicFieldsComplete()) return;
                  e.target.style.backgroundColor = 'var(--color-white)';
                  e.target.style.color = 'var(--color-primary)';
                  e.target.style.borderColor = 'var(--color-primary)';
                }}
                onMouseLeave={(e) => {
                  if (updateBondMutation.isPending || !areBasicFieldsComplete()) return;
                  e.target.style.backgroundColor = 'var(--color-primary)';
                  e.target.style.color = 'white';
                  e.target.style.borderColor = 'var(--color-primary)';
                }}
              >
                {updateBondMutation.isPending ? (
                  <LoadingSpinner size="sm" />
                ) : (
                  <>
                    Next
                    <ChevronRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );

  const renderStep3 = () => {
    if (selectedBondType === "fixed_notes") {
      return renderFixedNotesStep();
    } else {
      return renderSukukStep();
    }
  };

  // Check if all required fields are filled for Basic Information (Step 2)
  const areBasicFieldsComplete = () => {
    const values = watch();
    const requiredFields = [
      'bondName',
      'locationCode',
      'scriplessFlag',
      'isESGBond',
      'issueDate',
      'maturityDate',
      'issueAmount',
      'issuePrice',
      'currency',
      'minimumInvestmentValue',
    ];

    // Ensure each required field is present and non-empty
    for (const key of requiredFields) {
      const v = values[key];
      // Allow boolean false for isESGBond; reject only undefined/null/empty-string
      if (v === undefined || v === null || v === '') return false;
    }

    // Additional numeric validations
    const issueAmount = Number(values.issueAmount);
    const issuePrice = Number(values.issuePrice);
    const minInv = Number(values.minimumInvestmentValue);

    if (Number.isNaN(issueAmount) || issueAmount < 1000) return false;
    if (Number.isNaN(issuePrice) || !Number.isInteger(issuePrice) || issuePrice < 50 || issuePrice > 150) return false;
    if (Number.isNaN(minInv) || minInv < 1000) return false;

    // Check maturity date is after issue date
    if (values.issueDate && values.maturityDate) {
      if (new Date(values.maturityDate) <= new Date(values.issueDate)) return false;
    }

    // Check for any validation errors
    if (errors.maturityDate || errors.issueDate || errors.bondName || errors.locationCode ||
      errors.scriplessFlag || errors.issueAmount || errors.issuePrice || errors.minimumInvestmentValue) {
      return false;
    }

    // ESG certification upload is optional regardless of ESG selection
    return true;
  };

  // Get validation errors for Basic Information
  const getBasicFieldsErrors = () => {
    const errors = [];
    const values = watch();

    if (!values.bondName) errors.push('Bond Name is required');
    if (!values.locationCode) errors.push('Location Code is required');
    if (values.scriplessFlag === undefined || values.scriplessFlag === null || values.scriplessFlag === '') errors.push('Scripless Issuance selection is required');
    if (!values.issueDate) errors.push('Issue Date is required');
    if (!values.maturityDate) errors.push('Maturity Date is required');
    if (!values.issueAmount) errors.push('Issue Amount is required');
    if (!values.issuePrice) errors.push('Issue Price is required');
    if (!values.minimumInvestmentValue) errors.push('Minimum Investment Unit is required');
    return errors;
  };

  // Get validation errors for Conventional bonds
  const getConventionalFieldsErrors = () => {
    const errors = [];
    const formValues = watch();

    if (!formValues.couponRate || formValues.couponRate.toString().trim() === '') {
      errors.push('Coupon Rate is required');
    }
    if (!formValues.couponFrequency || formValues.couponFrequency.toString().trim() === '') {
      errors.push('Coupon Payment Frequency is required');
    }
    if (!uploadedFiles.riskAssessment) {
      errors.push('Default Risk Assessment Document is required');
    }
    if (!uploadedFiles.legalDocuments) {
      errors.push('Relevant Legal Documents are required');
    }
    if (!uploadedFiles.covenantsDocuments) {
      errors.push('Covenants Documents are required');
    }

    return errors;
  };

  // Get validation errors for Sukuk bonds
  const getSukukFieldsErrors = () => {
    const errors = [];
    const formValues = watch();

    if (!formValues.profitRate || formValues.profitRate.toString().trim() === '') {
      errors.push('Profit Rate is required');
    }
    if (!formValues.sukukStructureType || formValues.sukukStructureType.toString().trim() === '') {
      errors.push('Sukuk Structure Type is required');
    }
    if (!formValues.couponFrequency || formValues.couponFrequency.toString().trim() === '') {
      errors.push('Payment Frequency is required');
    }
    if (!formValues.islamicFinancingRating || formValues.islamicFinancingRating.toString().trim() === '') {
      errors.push('Islamic Financing Rating is required');
    }
    if (!formValues.sukukAssetType || formValues.sukukAssetType.toString().trim() === '') {
      errors.push('Sukuk Asset Type is required');
    }
    if (!formValues.assetLocation || formValues.assetLocation.toString().trim() === '') {
      errors.push('Asset Location is required');
    }
    if (!formValues.assetDescription || formValues.assetDescription.toString().trim() === '') {
      errors.push('Asset Description is required');
    }
    if (!formValues.assetOwnershipStructure || formValues.assetOwnershipStructure.toString().trim() === '') {
      errors.push('Asset Ownership Structure is required');
    }
    if (!formValues.shariahBoardName || formValues.shariahBoardName.toString().trim() === '') {
      errors.push('Shariah Board Name is required');
    }
    if (!formValues.fatwaReference || formValues.fatwaReference.toString().trim() === '') {
      errors.push('Fatwa Reference is required');
    }
    if (!formValues.shariahApprovalDate || formValues.shariahApprovalDate.toString().trim() === '') {
      errors.push('Shariah Approval Date is required');
    }
    if (!uploadedFiles.valuationReport) {
      errors.push('Valuation Report is required');
    }
    if (!uploadedFiles.legalDocuments) {
      errors.push('Legal Documents are required');
    }
    if (!uploadedFiles.covenantsDocuments) {
      errors.push('Covenants Documents are required');
    }

    return errors;
  };

  // Check if all required fields are filled for conventional bonds
  const areConventionalFieldsComplete = () => {
    const requiredFields = [
      "couponRate",
      "couponFrequency",
    ];
    const formValues = watch();

    // Check all form fields are filled
    const fieldsComplete = requiredFields.every(
      (field) => formValues[field] && formValues[field].toString().trim() !== ""
    );

    // Check required documents are uploaded
    const documentsUploaded =
      uploadedFiles.riskAssessment && uploadedFiles.legalDocuments && uploadedFiles.covenantsDocuments;

    // Debug logging
    console.log("Conventional validation:", {
      formValues,
      fieldsComplete,
      documentsUploaded,
      uploadedFiles,
      missingFields: requiredFields.filter(
        (field) =>
          !formValues[field] || formValues[field].toString().trim() === ""
      ),
    });

    return fieldsComplete && documentsUploaded;
  };

  // Check if all required fields are filled for sukuk bonds
  const areSukukFieldsComplete = () => {
    const requiredFields = [
      "profitRate",
      "sukukStructureType",
      "couponFrequency",
      "islamicFinancingRating",
      "sukukAssetType",
      "assetLocation",
      "assetDescription",
      "assetOwnershipStructure",
      "shariahBoardName",
      "fatwaReference",
      "shariahApprovalDate",
    ];
    const formValues = watch();

    // Check all form fields are filled
    const fieldsComplete = requiredFields.every(
      (field) => formValues[field] && formValues[field].toString().trim() !== ""
    );

    // Check required documents are uploaded
    const documentsUploaded =
      uploadedFiles.valuationReport &&
      uploadedFiles.legalDocuments &&
      uploadedFiles.covenantsDocuments;

    // Debug logging
    console.log("Sukuk validation:", {
      formValues,
      fieldsComplete,
      documentsUploaded,
      uploadedFiles,
      missingFields: requiredFields.filter(
        (field) =>
          !formValues[field] || formValues[field].toString().trim() === ""
      ),
    });

    return fieldsComplete && documentsUploaded;
  };

  // Check if all required fields are filled for token information
  const areTokenFieldsComplete = () => {
    const formValues = watch();

    // Check all required fields
    if (!formValues.tokenWallet || formValues.tokenWallet.trim() === "") return false;
    if (!formValues.tokenName || formValues.tokenName.trim() === "" || formValues.tokenName.length < 3) return false;
    if (!formValues.tokenSymbol || formValues.tokenSymbol.trim() === "" || formValues.tokenSymbol.length < 3 || formValues.tokenSymbol.length > 6) return false;
    if (formValues.tokenDecimals === undefined || formValues.tokenDecimals === null || formValues.tokenDecimals === "") return false;
    if (!formValues.faceValuePerToken || formValues.faceValuePerToken === "" || Number(formValues.faceValuePerToken) < 0.01) return false;

    // Validate numeric ranges
    const decimals = Number(formValues.tokenDecimals);
    if (isNaN(decimals) || decimals < 0 || decimals > 18) return false;

    return true;
  };

  const renderFixedNotesStep = () => (
    <div className="max-w-4xl mx-auto">
      <div className="card">
        <div className="card-header">
          <h2 className="text-lg font-medium text-dark-gray">
            Bond Details
          </h2>
          <p className="mt-1 text-sm text-secondary-600">
            Provide the specific terms for your Conventional bond
          </p>
        </div>
        <div className="card-body">
          <form onSubmit={handleSubmit(handleNext)} className="space-y-6">
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              {/* Coupon Rate */}
              <div>
                <label className="block mb-2 text-sm font-medium text-dark-green">
                  Coupon Rate (Annual %){" "}
                  <span className="required-asterisk">*</span>
                </label>
                <input
                  type="number"
                  step="0.01"
                  onKeyDown={(e) => {
                    if (e.key === 'e' || e.key === 'E') {
                      e.preventDefault();
                    }
                  }}
                  {...register("couponRate", {
                    required: "Coupon rate is required",
                    min: { value: 0, message: "Rate cannot be negative" },
                    max: { value: 100, message: "Rate cannot exceed 100%" },
                    validate: {
                      isNumber: (value) => {
                        const num = parseFloat(value);
                        return !isNaN(num) && isFinite(num) || "Please enter a valid number (integers or decimals only)";
                      }
                    }
                  })}
                  className="w-full px-3 py-2 border rounded-md border-light-gray focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="5.0"
                />
                {errors.couponRate && (
                  <p className="mt-1 text-sm text-error-600">
                    {errors.couponRate.message}
                  </p>
                )}
              </div>

              {/* Payment Frequency */}
              <div>
                <label className="block mb-2 text-sm font-medium text-dark-green">
                  Coupon Payment Frequency{" "}
                  <span className="required-asterisk">*</span>
                </label>
                <select
                  {...register("couponFrequency", {
                    required: "Payment frequency is required",
                  })}
                  className="w-full px-3 py-2 border rounded-md border-light-gray focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">Select frequency</option>
                  <option value="Monthly">Monthly</option>
                  <option value="Quarterly">Quarterly</option>
                  <option value="Semi-Annually">Semi-Annually</option>
                  <option value="Annually">Annually</option>
                  <option value="At Maturity">At Maturity</option>
                </select>
                {errors.couponFrequency && (
                  <p className="mt-1 text-sm text-error-600">
                    {errors.couponFrequency.message}
                  </p>
                )}
              </div>



              {/* Credit Rating */}
              <div>
                <label className="block mb-2 text-sm font-medium text-dark-green">
                  Credit Rating (Optional)
                </label>
                <select
                  {...register("creditRating")}
                  className="w-full px-3 py-2 border rounded-md border-light-gray focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">Select rating</option>
                  <option value="AAA">AAA</option>
                  <option value="AA">AA</option>
                  <option value="A">A</option>
                  <option value="BBB">BBB</option>
                  <option value="BB">BB</option>
                  <option value="B">B</option>
                  <option value="CCC">CCC</option>
                  <option value="Unrated">Unrated</option>
                </select>
              </div>
            </div>

            {/* Document Uploads */}
            <div className="mt-6 space-y-4">
              <h3 className="text-lg font-semibold text-secondary-900">
                Required Documents
              </h3>

              {/* Risk Assessment Document */}
              <div>
                <label className="block mb-2 text-sm font-medium text-dark-green">
                  Default Risk Assessment Document{" "}
                  <span className="required-asterisk">*</span>
                </label>
                <div className="p-4 border-2 border-dashed rounded-lg border-light-gray">
                  <input
                    type="file"
                    accept=".pdf"
                    onChange={(e) => handleFileUpload(e, "riskAssessment")}
                    className="hidden"
                    id="risk-assessment"
                  />
                  {uploadedFiles.riskAssessment ? (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <Upload className="w-8 h-8 mr-3 text-primary" />
                        <div>
                          <span className="text-sm font-medium text-secondary-900">
                            {uploadedFiles.riskAssessment.name}
                          </span>
                          <p className="text-xs text-secondary-500">
                            {(
                              uploadedFiles.riskAssessment.size /
                              1024 /
                              1024
                            ).toFixed(2)}{" "}
                            MB
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <label
                          htmlFor="risk-assessment"
                          className="px-3 py-1 text-xs rounded cursor-pointer bg-primary-50 text-primary-600 hover:bg-primary-100"
                        >
                          Replace
                        </label>
                        <button
                          type="button"
                          onClick={() => handleFileDelete("riskAssessment")}
                          className="p-1 rounded text-error-600 hover:bg-error-50"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <label
                      htmlFor="risk-assessment"
                      className="flex flex-col items-center cursor-pointer"
                    >
                      <Upload className="w-8 h-8 mb-2 text-primary" />
                      <span className="text-sm text-secondary-600">
                        Click to upload PDF
                      </span>
                    </label>
                  )}
                </div>
                {!uploadedFiles.riskAssessment && (
                  <p className="mt-2 text-sm text-error-600">
                    <AlertTriangle className="inline w-4 h-4 mr-1" />
                    This document is required
                  </p>
                )}
              </div>

              {/* Legal Documents */}
              <div>
                <label className="block mb-2 text-sm font-medium text-dark-green">
                  Relevant Legal Documents{" "}
                  <span className="required-asterisk">*</span>
                </label>
                <div className="p-4 border-2 border-dashed rounded-lg border-light-gray">
                  <input
                    type="file"
                    accept=".pdf"
                    multiple
                    onChange={(e) => handleFileUpload(e, "legalDocuments")}
                    className="hidden"
                    id="legal-documents"
                  />
                  {uploadedFiles.legalDocuments ? (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <Upload className="w-8 h-8 mr-3 text-primary" />
                        <div>
                          <span className="text-sm font-medium text-secondary-900">
                            {Array.isArray(uploadedFiles.legalDocuments)
                              ? `${uploadedFiles.legalDocuments.length} file(s) selected`
                              : uploadedFiles.legalDocuments.name}
                          </span>
                          <p className="text-xs text-secondary-500">
                            {Array.isArray(uploadedFiles.legalDocuments)
                              ? uploadedFiles.legalDocuments
                                .map((f) => f.name)
                                .join(", ")
                              : `${(
                                uploadedFiles.legalDocuments.size /
                                1024 /
                                1024
                              ).toFixed(2)} MB`}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <label
                          htmlFor="legal-documents"
                          className="px-3 py-1 text-xs rounded cursor-pointer bg-primary-50 text-primary-600 hover:bg-primary-100"
                        >
                          Replace
                        </label>
                        <button
                          type="button"
                          onClick={() => handleFileDelete("legalDocuments")}
                          className="p-1 rounded text-error-600 hover:bg-error-50"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <label
                      htmlFor="legal-documents"
                      className="flex flex-col items-center cursor-pointer"
                    >
                      <Upload className="w-8 h-8 mb-2 text-primary" />
                      <span className="text-sm text-secondary-600">
                        Click to upload legal documents (PDF)
                      </span>
                    </label>
                  )}
                </div>
                {!uploadedFiles.legalDocuments && (
                  <p className="mt-2 text-sm text-error-600">
                    <AlertTriangle className="inline w-4 h-4 mr-1" />
                    This document is required
                  </p>
                )}
              </div>

              {/* Covenants Documents */}
              <div>
                <label className="block mb-2 text-sm font-medium text-dark-green">
                  Covenants Documents <span className="required-asterisk">*</span>
                </label>
                <div className="p-4 border-2 border-dashed rounded-lg border-light-gray">
                  <input
                    type="file"
                    accept=".pdf"
                    onChange={(e) => handleFileUpload(e, "covenantsDocuments")}
                    className="hidden"
                    id="covenants-documents"
                  />
                  {uploadedFiles.covenantsDocuments ? (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <Upload className="w-8 h-8 mr-3 text-primary" />
                        <div>
                          <span className="text-sm font-medium text-secondary-900">
                            {uploadedFiles.covenantsDocuments.name}
                          </span>
                          <p className="text-xs text-secondary-500">
                            {(
                              uploadedFiles.covenantsDocuments.size /
                              1024 /
                              1024
                            ).toFixed(2)} MB
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <label
                          htmlFor="covenants-documents"
                          className="px-3 py-1 text-xs rounded cursor-pointer bg-primary-50 text-primary-600 hover:bg-primary-100"
                        >
                          Replace
                        </label>
                        <button
                          type="button"
                          onClick={() => handleFileDelete("covenantsDocuments")}
                          className="p-1 rounded text-error-600 hover:bg-error-50"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <label
                      htmlFor="covenants-documents"
                      className="flex flex-col items-center cursor-pointer"
                    >
                      <Upload className="w-8 h-8 mb-2 text-primary" />
                      <span className="text-sm text-secondary-600">
                        Click to upload covenants document (PDF)
                      </span>
                    </label>
                  )}
                </div>
                {!uploadedFiles.covenantsDocuments && (
                  <p className="mt-2 text-sm text-error-600">
                    <AlertTriangle className="inline w-4 h-4 mr-1" />
                    This document is required
                  </p>
                )}
              </div>
            </div>

            <div className="flex justify-between">
              <button
                type="button"
                onClick={() => setCurrentStep(2)}
                className="flex items-center px-6 py-3 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                style={{
                  fontFamily: '"Roboto", sans-serif',
                  fontWeight: 700,
                  backgroundColor: "var(--color-gray)",
                  color: "var(--color-dark-gray)",
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = "var(--color-medium-gray)";
                  e.target.style.color = "var(--color-dark-gray)";
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = "var(--color-gray)";
                  e.target.style.color = "var(--color-dark-gray)";
                }}
              >
                <ChevronLeft className="w-4 h-4 mr-2" />
                Back
              </button>
              <button
                type="submit"
                disabled={
                  updateBondMutation.isPending ||
                  !areConventionalFieldsComplete()
                }
                className="flex items-center px-6 py-3 transition-colors border rounded focus:outline-none focus:ring-2 focus:ring-offset-2"
                style={{
                  fontFamily: '"Roboto", sans-serif',
                  fontWeight: 700,
                  backgroundColor: (updateBondMutation.isPending || !areConventionalFieldsComplete()) ? 'var(--color-medium-gray)' : 'var(--color-primary)',
                  borderColor: (updateBondMutation.isPending || !areConventionalFieldsComplete()) ? 'var(--color-medium-gray)' : 'var(--color-primary)',
                  color: (updateBondMutation.isPending || !areConventionalFieldsComplete()) ? 'var(--color-dark-gray)' : 'white',
                  cursor: (updateBondMutation.isPending || !areConventionalFieldsComplete()) ? 'not-allowed' : 'pointer',
                  opacity: (updateBondMutation.isPending || !areConventionalFieldsComplete()) ? 0.8 : 1,
                }}
                onMouseEnter={(e) => {
                  if (updateBondMutation.isPending || !areConventionalFieldsComplete()) return;
                  e.target.style.backgroundColor = "var(--color-white)";
                  e.target.style.color = "var(--color-primary)";
                  e.target.style.borderColor = "var(--color-primary)";
                }}
                onMouseLeave={(e) => {
                  if (updateBondMutation.isPending || !areConventionalFieldsComplete()) return;
                  e.target.style.backgroundColor = "var(--color-primary)";
                  e.target.style.color = "white";
                  e.target.style.borderColor = "var(--color-primary)";
                }}
              >
                {updateBondMutation.isPending ? (
                  <LoadingSpinner size="sm" />
                ) : (
                  <>
                    Next
                    <ChevronRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );

  const renderSukukStep = () => (
    <div className="max-w-4xl mx-auto">
      <div className="card">
        <div className="card-header">
          <h2 className="text-lg font-medium text-secondary-900">
            Bond Details
          </h2>
          <p className="mt-1 text-sm text-secondary-600">
            Provide the structure, asset, and compliance information for your
            Sukuk
          </p>
        </div>
        <div className="card-body">
          <form onSubmit={handleSubmit(handleNext)} className="space-y-6">
            {/* Sukuk Structure & Profit */}
            <div>
              <h3 className="mb-4 text-lg font-semibold text-secondary-900">
                Sukuk Structure & Profit
              </h3>
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                {/* Profit Rate */}
                <div>
                  <label className="block mb-2 text-sm font-medium text-dark-green">
                    profit rate/discount/floating rate{" "}
                    <span className="required-asterisk">*</span>
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    onKeyDown={(e) => {
                      if (e.key === 'e' || e.key === 'E') {
                        e.preventDefault();
                      }
                    }}
                    {...register("profitRate", {
                      required: "Profit rate is required",
                      min: { value: 0, message: "Rate cannot be negative" },
                      max: { value: 100, message: "Rate cannot exceed 100%" },
                    })}
                    className="w-full px-3 py-2 border rounded-md border-light-gray focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="5.0"
                  />
                  {errors.profitRate && (
                    <p className="mt-1 text-sm text-error-600">
                      {errors.profitRate.message}
                    </p>
                  )}
                </div>

                {/* Sukuk Structure Type */}
                <div>
                  <label className="block mb-2 text-sm font-medium text-dark-green">
                    Sukuk Structure Type{" "}
                    <span className="required-asterisk">*</span>
                  </label>
                  <select
                    {...register("sukukStructureType", {
                      required: "Structure type is required",
                    })}
                    className="w-full px-3 py-2 border rounded-md border-light-gray focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="">Select structure</option>
                    <option value="Ijarah">Sukuk Ijarah</option>
                    <option value="Murabahah">Sukuk Murabahah</option>
                    <option value="Musharakah">Sukuk Musharakah</option>
                    <option value="Wakalah">Sukuk Wakalah</option>
                    <option value="Other">Other</option>
                  </select>
                  {errors.sukukStructureType && (
                    <p className="mt-1 text-sm text-error-600">
                      {errors.sukukStructureType.message}
                    </p>
                  )}
                </div>

                {/* Profit Distribution Frequency */}
                <div>
                  <label className="block mb-2 text-sm font-medium text-dark-green">
                    Profit Distribution Frequency{" "}
                    <span className="required-asterisk">*</span>
                  </label>
                  <select
                    {...register("couponFrequency", {
                      required: "Distribution frequency is required",
                    })}
                    className="w-full px-3 py-2 border rounded-md border-light-gray focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="">Select frequency</option>
                    <option value="Monthly">Monthly</option>
                    <option value="Quarterly">Quarterly</option>
                    <option value="Semi-Annually">Semi-Annually</option>
                    <option value="Annually">Annually</option>
                    <option value="At Maturity">At Maturity</option>
                  </select>
                  {errors.couponFrequency && (
                    <p className="mt-1 text-sm text-error-600">
                      {errors.couponFrequency.message}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block mb-2 text-sm font-medium text-dark-green">
                    Islamic Financing Rating{" "}
                    <span className="required-asterisk">*</span>
                  </label>
                  <select
                    {...register("islamicFinancingRating", {
                      required: "Islamic financing rating is required",
                    })}
                    className="w-full px-3 py-2 border rounded-md border-light-gray focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="">Select rating</option>
                    <option value="AAA">AAA</option>
                    <option value="AA+">AA+</option>
                    <option value="AA">AA</option>
                    <option value="AA-">AA-</option>
                    <option value="A+">A+</option>
                    <option value="A">A</option>
                    <option value="A-">A-</option>
                    <option value="BBB+">BBB+</option>
                    <option value="BBB">BBB</option>
                    <option value="BBB-">BBB-</option>
                  </select>
                  {errors.islamicFinancingRating && (
                    <p className="mt-1 text-sm text-error-600">
                      {errors.islamicFinancingRating.message}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Underlying Asset Information */}
            <div>
              <h3 className="mb-4 text-lg font-semibold text-secondary-900">
                Underlying Asset Information
              </h3>
              <div className="space-y-4">
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                  {/* Sukuk Asset Type */}
                  <div>
                    <label className="block mb-2 text-sm font-medium text-dark-green">
                      Sukuk Asset Type{" "}
                      <span className="required-asterisk">*</span>
                    </label>
                    <select
                      {...register("sukukAssetType", {
                        required: "Sukuk asset type is required",
                      })}
                      className="w-full px-3 py-2 border rounded-md border-light-gray focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="">Select asset type</option>
                      <option value="Real Estate">Real Estate</option>
                      <option value="Infrastructure">Infrastructure</option>
                      <option value="Equipment">Equipment</option>
                      <option value="Commodities">Commodities</option>
                      <option value="Other">Other</option>
                    </select>
                    {errors.sukukAssetType && (
                      <p className="mt-1 text-sm text-error-600">
                        {errors.sukukAssetType.message}
                      </p>
                    )}
                  </div>

                  {/* Asset Location */}
                  <div>
                    <label className="block mb-2 text-sm font-medium text-dark-green">
                      Asset Location{" "}
                      <span className="required-asterisk">*</span>
                    </label>
                    <input
                      type="text"
                      {...register("assetLocation", {
                        required: "Asset location is required",
                      })}
                      className="w-full px-3 py-2 border rounded-md border-light-gray focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                      placeholder="Location of the underlying asset"
                    />
                    {errors.assetLocation && (
                      <p className="mt-1 text-sm text-error-600">
                        {errors.assetLocation.message}
                      </p>
                    )}
                  </div>
                </div>
                {/* Asset Description */}
                <div>
                  <label className="block mb-2 text-sm font-medium text-dark-green">
                    Underlying Asset Description{" "}
                    <span className="required-asterisk">*</span>
                  </label>
                  <textarea
                    {...register("assetDescription", {
                      required: "Asset description is required",
                    })}
                    rows={4}
                    className="w-full px-3 py-2 border rounded-md border-light-gray focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Detailed description of the asset supporting the Sukuk"
                  />
                  {errors.assetDescription && (
                    <p className="mt-1 text-sm text-error-600">
                      {errors.assetDescription.message}
                    </p>
                  )}
                </div>
                {/* Asset Ownership Structure */}
                <div>
                  <label className="block mb-2 text-sm font-medium text-dark-green">
                    Asset Ownership Structure{" "}
                    <span className="required-asterisk">*</span>
                  </label>
                  <textarea
                    {...register("assetOwnershipStructure", {
                      required: "Ownership structure is required",
                    })}
                    rows={3}
                    className="w-full px-3 py-2 border rounded-md border-light-gray focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Describe the ownership and legal structure of the asset"
                  />
                  {errors.assetOwnershipStructure && (
                    <p className="mt-1 text-sm text-error-600">
                      {errors.assetOwnershipStructure.message}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Shariah Compliance */}
            <div>
              <h3 className="mb-4 text-lg font-semibold text-secondary-900">
                Shariah Compliance
              </h3>
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                {/* Shariah Board Name */}
                <div>
                  <label className="block mb-2 text-sm font-medium text-dark-green">
                    Shariah Board Name{" "}
                    <span className="required-asterisk">*</span>
                  </label>
                  <input
                    type="text"
                    {...register("shariahBoardName", {
                      required: "Shariah board name is required",
                    })}
                    className="w-full px-3 py-2 border rounded-md border-light-gray focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Name of the Shariah advisory board"
                  />
                  {errors.shariahBoardName && (
                    <p className="mt-1 text-sm text-error-600">
                      {errors.shariahBoardName.message}
                    </p>
                  )}
                </div>

                {/* Fatwa Reference */}
                <div>
                  <label className="block mb-2 text-sm font-medium text-dark-green">
                    Fatwa Reference Number{" "}
                    <span className="required-asterisk">*</span>
                  </label>
                  <input
                    type="text"
                    {...register("fatwaReference", {
                      required: "Fatwa reference is required",
                    })}
                    className="w-full px-3 py-2 border rounded-md border-light-gray focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Fatwa reference number"
                  />
                  {errors.fatwaReference && (
                    <p className="mt-1 text-sm text-error-600">
                      {errors.fatwaReference.message}
                    </p>
                  )}
                </div>

                {/* Shariah Board Approval Date */}
                <div>
                  <label className="block mb-2 text-sm font-medium text-dark-green">
                    Shariah Board Approval Date{" "}
                    <span className="required-asterisk">*</span>
                  </label>
                  <input
                    type="date"
                    {...register("shariahApprovalDate", {
                      required: "Approval date is required",
                    })}
                    className="w-full px-3 py-2 border rounded-md border-light-gray focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                  />
                  {errors.shariahApprovalDate && (
                    <p className="mt-1 text-sm text-error-600">
                      {errors.shariahApprovalDate.message}
                    </p>
                  )}
                </div>
              </div>

              {/* Document Uploads */}
              <div className="mt-6 space-y-4">
                <h4 className="font-semibold text-md text-secondary-900">
                  Required Documents
                </h4>

                {/* Asset Valuation Report */}
                <div>
                  <label className="block mb-2 text-sm font-medium text-dark-green">
                    Asset Valuation Report{" "}
                    <span className="required-asterisk">*</span>
                  </label>
                  <div className="p-4 border-2 border-dashed rounded-lg border-light-gray">
                    <input
                      type="file"
                      accept=".pdf"
                      onChange={(e) => handleFileUpload(e, "valuationReport")}
                      className="hidden"
                      id="valuation-report"
                    />
                    <label
                      htmlFor="valuation-report"
                      className="flex flex-col items-center cursor-pointer"
                    >
                      <Upload className="w-8 h-8 mb-2 text-primary" />
                      <span className="text-sm text-secondary-600">
                        {uploadedFiles.valuationReport
                          ? uploadedFiles.valuationReport.name
                          : "Click to upload PDF"}
                      </span>
                    </label>
                  </div>
                  {!uploadedFiles.valuationReport && (
                    <p className="mt-2 text-sm text-error-600">
                      <AlertTriangle className="inline w-4 h-4 mr-1" />
                      This document is required
                    </p>
                  )}
                </div>

                {/* Legal Documents */}
                <div>
                  <label className="block mb-2 text-sm font-medium text-dark-green">
                    Relevant Legal Documents{" "}
                    <span className="required-asterisk">*</span>
                  </label>
                  <div className="p-4 border-2 border-dashed rounded-lg border-light-gray">
                    <input
                      type="file"
                      accept=".pdf"
                      multiple
                      onChange={(e) => handleFileUpload(e, "legalDocuments")}
                      className="hidden"
                      id="sukuk-legal-documents"
                    />
                    <label
                      htmlFor="sukuk-legal-documents"
                      className="flex flex-col items-center cursor-pointer"
                    >
                      <Upload className="w-8 h-8 mb-2 text-primary" />
                      <span className="text-sm text-secondary-600">
                        {uploadedFiles.legalDocuments
                          ? `${uploadedFiles.legalDocuments.length || 1
                          } file(s) selected`
                          : "Click to upload legal documents (PDF)"}
                      </span>
                    </label>
                  </div>
                  {!uploadedFiles.legalDocuments && (
                    <p className="mt-2 text-sm text-error-600">
                      <AlertTriangle className="inline w-4 h-4 mr-1" />
                      This document is required
                    </p>
                  )}
                </div>

                {/* Covenants Documents */}
                <div>
                  <label className="block mb-2 text-sm font-medium text-dark-green">
                    Covenants Documents <span className="required-asterisk">*</span>
                  </label>
                  <div className="p-4 border-2 border-dashed rounded-lg border-light-gray">
                    <input
                      type="file"
                      accept=".pdf"
                      onChange={(e) => handleFileUpload(e, "covenantsDocuments")}
                      className="hidden"
                      id="sukuk-covenants-documents"
                    />
                    <label
                      htmlFor="sukuk-covenants-documents"
                      className="flex flex-col items-center cursor-pointer"
                    >
                      <Upload className="w-8 h-8 mb-2 text-primary" />
                      <span className="text-sm text-secondary-600">
                        {uploadedFiles.covenantsDocuments
                          ? uploadedFiles.covenantsDocuments.name
                          : "Click to upload covenants document (PDF)"}
                      </span>
                    </label>
                  </div>
                  {!uploadedFiles.covenantsDocuments && (
                    <p className="mt-2 text-sm text-error-600">
                      <AlertTriangle className="inline w-4 h-4 mr-1" />
                      This document is required
                    </p>
                  )}
                </div>

                {/* Optional Documents Section */}
                <div className="pt-6 mt-8 border-t border-secondary-200">
                  <h4 className="mb-4 font-semibold text-md text-secondary-900">
                    Optional Documents
                  </h4>
                  <div className="space-y-4">
                    {/* Securities Lodgement Form */}
                    <div>
                      <label className="block mb-2 text-sm font-medium text-dark-green">
                        Securities Lodgement Form
                        <span className="ml-1 text-xs text-secondary-500">
                          (Optional)
                        </span>
                      </label>
                      <div className="p-4 border-2 border-dashed rounded-lg border-light-gray">
                        <input
                          type="file"
                          accept=".pdf,.doc,.docx"
                          onChange={(e) =>
                            handleFileUpload(e, "securitiesLodgementForm")
                          }
                          className="hidden"
                          id="securities-lodgement-form"
                        />
                        <label
                          htmlFor="securities-lodgement-form"
                          className="flex flex-col items-center cursor-pointer"
                        >
                          <Upload className="w-8 h-8 mb-2 text-primary" />
                          <span className="text-sm text-secondary-600">
                            {uploadedFiles.securitiesLodgementForm
                              ? uploadedFiles.securitiesLodgementForm.name
                              : "Click to upload PDF/DOC"}
                          </span>
                        </label>
                      </div>
                    </div>

                    {/* Global Certificate */}
                    <div>
                      <label className="block mb-2 text-sm font-medium text-dark-green">
                        Global Certificate
                        <span className="ml-1 text-xs text-secondary-500">
                          (Optional)
                        </span>
                      </label>
                      <div className="p-4 border-2 border-dashed rounded-lg border-light-gray">
                        <input
                          type="file"
                          accept=".pdf,.doc,.docx"
                          onChange={(e) =>
                            handleFileUpload(e, "globalCertificate")
                          }
                          className="hidden"
                          id="global-certificate"
                        />
                        <label
                          htmlFor="global-certificate"
                          className="flex flex-col items-center cursor-pointer"
                        >
                          <Upload className="w-8 h-8 mb-2 text-primary" />
                          <span className="text-sm text-secondary-600">
                            {uploadedFiles.globalCertificate
                              ? uploadedFiles.globalCertificate.name
                              : "Click to upload PDF/DOC"}
                          </span>
                        </label>
                      </div>
                    </div>

                    {/* Debt Facility / Securities Information Form */}
                    <div>
                      <label className="block mb-2 text-sm font-medium text-dark-green">
                        Debt Facility / Securities Information Form
                        <span className="ml-1 text-xs text-secondary-500">
                          (Optional)
                        </span>
                      </label>
                      <div className="p-4 border-2 border-dashed rounded-lg border-light-gray">
                        <input
                          type="file"
                          accept=".pdf,.doc,.docx"
                          onChange={(e) =>
                            handleFileUpload(e, "debtFacilityForm")
                          }
                          className="hidden"
                          id="debt-facility-form"
                        />
                        <label
                          htmlFor="debt-facility-form"
                          className="flex flex-col items-center cursor-pointer"
                        >
                          <Upload className="w-8 h-8 mb-2 text-primary" />
                          <span className="text-sm text-secondary-600">
                            {uploadedFiles.debtFacilityForm
                              ? uploadedFiles.debtFacilityForm.name
                              : "Click to upload PDF/DOC"}
                          </span>
                        </label>
                      </div>
                    </div>

                    {/* Shariah Compliance Certificate */}
                    <div>
                      <label className="block mb-2 text-sm font-medium text-dark-green">
                        Shariah Compliance Certificate
                        <span className="ml-1 text-xs text-secondary-500">
                          (Optional)
                        </span>
                      </label>
                      <div className="p-4 border-2 border-dashed rounded-lg border-light-gray">
                        <input
                          type="file"
                          accept=".pdf"
                          onChange={(e) =>
                            handleFileUpload(e, "shariahCertificate")
                          }
                          className="hidden"
                          id="shariah-certificate"
                        />
                        {uploadedFiles.shariahCertificate ? (
                          <div className="flex items-center justify-between">
                            <div className="flex items-center">
                              <Upload className="w-8 h-8 mr-3 text-primary" />
                              <div>
                                <span className="text-sm font-medium text-secondary-900">
                                  {uploadedFiles.shariahCertificate.name}
                                </span>
                                <p className="text-xs text-secondary-500">
                                  {(
                                    uploadedFiles.shariahCertificate.size /
                                    1024 /
                                    1024
                                  ).toFixed(2)}{" "}
                                  MB
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center space-x-2">
                              <label
                                htmlFor="shariah-certificate"
                                className="px-3 py-1 text-xs rounded cursor-pointer bg-primary-50 text-primary-600 hover:bg-primary-100"
                              >
                                Replace
                              </label>
                              <button
                                type="button"
                                onClick={() =>
                                  handleFileDelete("shariahCertificate")
                                }
                                className="p-1 rounded text-error-600 hover:bg-error-50"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        ) : (
                          <label
                            htmlFor="shariah-certificate"
                            className="flex flex-col items-center cursor-pointer"
                          >
                            <Upload className="w-8 h-8 mb-2 text-primary" />
                            <span className="text-sm text-secondary-600">
                              Click to upload PDF
                            </span>
                          </label>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-between">
              <button
                type="button"
                onClick={() => setCurrentStep(2)}
                className="flex items-center px-6 py-3 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                style={{
                  fontFamily: '"Roboto", sans-serif',
                  fontWeight: 700,
                  backgroundColor: "var(--color-gray)",
                  color: "var(--color-dark-gray)",
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = "var(--color-medium-gray)";
                  e.target.style.color = "var(--color-dark-gray)";
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = "var(--color-gray)";
                  e.target.style.color = "var(--color-dark-gray)";
                }}
              >
                <ChevronLeft className="w-4 h-4 mr-2" />
                Back
              </button>
              <button
                type="submit"
                disabled={
                  updateBondMutation.isPending || !areSukukFieldsComplete()
                }
                className="flex items-center px-6 py-3 transition-colors border rounded focus:outline-none focus:ring-2 focus:ring-offset-2"
                style={{
                  fontFamily: '"Roboto", sans-serif',
                  fontWeight: 700,
                  backgroundColor: (updateBondMutation.isPending || !areSukukFieldsComplete()) ? 'var(--color-medium-gray)' : 'var(--color-primary)',
                  borderColor: (updateBondMutation.isPending || !areSukukFieldsComplete()) ? 'var(--color-medium-gray)' : 'var(--color-primary)',
                  color: (updateBondMutation.isPending || !areSukukFieldsComplete()) ? 'var(--color-dark-gray)' : 'white',
                  cursor: (updateBondMutation.isPending || !areSukukFieldsComplete()) ? 'not-allowed' : 'pointer',
                  opacity: (updateBondMutation.isPending || !areSukukFieldsComplete()) ? 0.8 : 1,
                }}
                onMouseEnter={(e) => {
                  if (updateBondMutation.isPending || !areSukukFieldsComplete()) return;
                  e.target.style.backgroundColor = "var(--color-white)";
                  e.target.style.color = "var(--color-primary)";
                  e.target.style.borderColor = "var(--color-primary)";
                }}
                onMouseLeave={(e) => {
                  if (updateBondMutation.isPending || !areSukukFieldsComplete()) return;
                  e.target.style.backgroundColor = "var(--color-primary)";
                  e.target.style.color = "white";
                  e.target.style.borderColor = "var(--color-primary)";
                }}
              >
                {updateBondMutation.isPending ? (
                  <LoadingSpinner size="sm" />
                ) : (
                  <>
                    Next
                    <ChevronRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );

  const renderStep4 = () => (
    <div className="max-w-4xl mx-auto">
      <div className="card">
        <div className="card-header">
          <h2 className="mb-4 text-3xl font-bold text-secondary-900">
            Token Information
          </h2>
          <p className="text-lg text-secondary-600">
            Please provide the technical details for your bond token.
          </p>
        </div>

        <div className="card-body">
          <form onSubmit={handleSubmit(handleNext)} className="space-y-6">
            <h3 className="mb-4 text-lg font-semibold text-secondary-900">
              Token Details
            </h3>

            {/* Token Creation Status */}
            {bondData?.data?.bond?.tokenDetails && (
              <div className="mb-6">
                {bondData.data.bond.tokenDetails.tokenCreationStatus ===
                  "created" &&
                  bondData.data.bond.tokenDetails.tokenAddress && (
                    <div className="p-4 border border-green-200 rounded-lg bg-green-50">
                      <div className="flex items-center">
                        <CheckCircle className="w-5 h-5 mr-2 text-green-600" />
                        <div>
                          <h4 className="text-sm font-medium text-green-800">
                            Token Created Successfully!
                          </h4>
                          <p className="mt-1 text-sm text-green-700">
                            Token Address:{" "}
                            <span className="font-mono text-xs">
                              {bondData.data.bond.tokenDetails.tokenAddress}
                            </span>
                          </p>
                          {bondData.data.bond.tokenDetails
                            .tokenTransactionHash && (
                              <p className="mt-1 text-sm text-green-700">
                                Transaction:{" "}
                                <span className="font-mono text-xs">
                                  {
                                    bondData.data.bond.tokenDetails
                                      .tokenTransactionHash
                                  }
                                </span>
                              </p>
                            )}
                        </div>
                      </div>
                    </div>
                  )}

                {bondData.data.bond.tokenDetails.tokenCreationStatus ===
                  "failed" && (
                    <div className="p-4 border border-red-200 rounded-lg bg-red-50">
                      <div className="flex items-center">
                        <AlertCircle className="w-5 h-5 mr-2 text-red-600" />
                        <div>
                          <h4 className="text-sm font-medium text-red-800">
                            Token Creation Failed
                          </h4>
                          <p className="mt-1 text-sm text-red-700">
                            {bondData.data.bond.tokenDetails.tokenCreationError ||
                              "An error occurred while creating the token."}
                          </p>
                          <p className="mt-1 text-sm text-red-700">
                            Please try updating the token information again or
                            contact support.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                {updateBondMutation.isPending && currentStep === 4 && (
                  <div className="p-4 border border-blue-200 rounded-lg bg-blue-50">
                    <div className="flex items-center">
                      <div className="w-5 h-5 mr-2 border-b-2 border-blue-600 rounded-full animate-spin"></div>
                      <div>
                        <h4 className="text-sm font-medium text-blue-800">
                          Creating Token...
                        </h4>
                        <p className="mt-1 text-sm text-blue-700">
                          Please wait while we create your bond token on the
                          blockchain.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              {/* Wallet */}
              <div className="md:col-span-2">
                <label className="block mb-2 text-sm font-medium text-dark-green">
                  Wallet for Token Creation{" "}
                  <span className="required-asterisk">*</span>
                </label>
                <div className="space-y-3">
                  <div className="flex gap-2">
                    <select
                      {...register("tokenWallet", {
                        required: "Please select a wallet for token creation",
                      })}
                      className="flex-1 px-3 py-2 border rounded-md border-light-gray focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                      disabled={walletLoading}
                    >
                      <option value="">
                        {walletLoading
                          ? "Loading wallet..."
                          : "Select existing wallet"}
                      </option>
                      {walletData?.data?.wallet && (
                        <option value={walletData.data.wallet.walletAddress}>
                          {walletData.data.wallet.walletAddress}
                        </option>
                      )}
                      {walletError && (
                        <option value="" disabled>
                          Error loading wallet
                        </option>
                      )}
                    </select>
                    {/* Wallet Management temporarily hidden for issuer */}
                  </div>
                  {errors.tokenWallet && (
                    <p className="mt-1 text-sm text-error-600">
                      {errors.tokenWallet.message}
                    </p>
                  )}
                  {walletError && (
                    <p className="mt-1 text-sm text-error-600">
                      Failed to load wallet information. Please try refreshing
                      the page or contact support.
                    </p>
                  )}
                  {!walletLoading &&
                    !walletError &&
                    !walletData?.data?.wallet && (
                      <p className="mt-1 text-sm text-warning-600">
                        No wallet found. Please create a wallet in Wallet
                        Management before proceeding.
                      </p>
                    )}
                  <p className="text-xs text-secondary-500">
                    This wallet will be used for token creation and management.
                  </p>
                </div>
              </div>

              {/* Token Name */}
              <div>
                <label className="block mb-2 text-sm font-medium text-dark-green">
                  Token Name <span className="required-asterisk">*</span>
                </label>
                <input
                  type="text"
                  {...register("tokenName", {
                    required: "Token name is required",
                    minLength: {
                      value: 3,
                      message: "Token name must be at least 3 characters",
                    },
                  })}
                  className="w-full px-3 py-2 border rounded-md border-light-gray focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="e.g., ABC Token"
                />
                {errors.tokenName && (
                  <p className="mt-1 text-sm text-error-600">
                    {errors.tokenName.message}
                  </p>
                )}
                <p className="mt-1 text-xs text-secondary-500">
                  Full name of the token as it will appear on the blockchain
                </p>
              </div>

              {/* Symbol */}
              <div>
                <label className="block mb-2 text-sm font-medium text-dark-green">
                  Symbol <span className="required-asterisk">*</span>
                </label>
                <input
                  type="text"
                  {...register("tokenSymbol", {
                    required: "Token symbol is required",
                    minLength: {
                      value: 3,
                      message: "Symbol must be at least 3 characters",
                    },
                    maxLength: {
                      value: 6,
                      message: "Symbol must be at most 6 characters",
                    },
                  })}
                  className="w-full px-3 py-2 border rounded-md border-light-gray focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="e.g., ABT"
                  style={{ textTransform: "uppercase" }}
                  onInput={(e) =>
                    (e.target.value = e.target.value.toUpperCase())
                  }
                />
                {errors.tokenSymbol && (
                  <p className="mt-1 text-sm text-error-600">
                    {errors.tokenSymbol.message}
                  </p>
                )}
                <p className="mt-1 text-xs text-secondary-500">
                  Short ticker symbol for the token
                </p>
              </div>

              {/* Decimal */}
              <div>
                <label className="block mb-2 text-sm font-medium text-dark-green">
                  Decimal <span className="required-asterisk">*</span>
                </label>
                <input
                  type="number"
                  onKeyDown={(e) => {
                    if (e.key === '.' || e.key === 'e' || e.key === 'E' || e.key === '+' || e.key === '-') {
                      e.preventDefault();
                    }
                  }}
                  {...register("tokenDecimals", {
                    required: "Token decimals are required",
                    min: { value: 0, message: "Minimum decimals is 0" },
                    max: { value: 18, message: "Maximum decimals is 18" },
                  })}
                  className="w-full px-3 py-2 border rounded-md border-light-gray focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="18"
                  defaultValue="18"
                />
                {errors.tokenDecimals && (
                  <p className="mt-1 text-sm text-error-600">
                    {errors.tokenDecimals.message}
                  </p>
                )}
                <p className="mt-1 text-xs text-secondary-500">
                  Number of decimal places (18 for standard ERC-20 tokens)
                </p>
              </div>

              {/* Face Value per Token */}
              <div>
                <label className="block mb-2 text-sm font-medium text-dark-green">
                  Face Value per Token{" "}
                  <span className="required-asterisk">*</span>
                </label>
                <input
                  type="number"
                  step="0.01"
                  onKeyDown={(e) => {
                    if (e.key === 'e' || e.key === 'E') {
                      e.preventDefault();
                    }
                  }}
                  {...register("faceValuePerToken", {
                    required: "Face value per token is required",
                    min: { value: 0.01, message: "Minimum face value is 0.01" },
                  })}
                  className="w-full px-3 py-2 border rounded-md border-light-gray focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="1000.00"
                />
                {errors.faceValuePerToken && (
                  <p className="mt-1 text-sm text-error-600">
                    {errors.faceValuePerToken.message}
                  </p>
                )}
                <p className="mt-1 text-xs text-secondary-500">
                  Nominal value of each individual token
                </p>
              </div>
            </div>

            <div className="flex justify-between">
              <button
                type="button"
                onClick={() => setCurrentStep(currentStep - 1)}
                className="flex items-center px-6 py-3 transition-colors rounded focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
                disabled={updateBondMutation.isPending || showTokenLoading}
                style={{
                  fontFamily: '"Roboto", sans-serif',
                  fontWeight: 700,
                  backgroundColor: "var(--color-gray)",
                  color: "var(--color-dark-gray)",
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = "var(--color-medium-gray)";
                  e.target.style.color = "var(--color-dark-gray)";
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = "var(--color-gray)";
                  e.target.style.color = "var(--color-dark-gray)";
                }}
              >
                <ChevronLeft className="w-4 h-4 mr-2" />
                Back
              </button>
              <button
                type="submit"
                disabled={updateBondMutation.isPending || showTokenLoading || !areTokenFieldsComplete()}
                className="flex items-center px-6 py-3 transition-colors border rounded focus:outline-none focus:ring-2 focus:ring-offset-2"
                style={{
                  fontFamily: '"Roboto", sans-serif',
                  fontWeight: 700,
                  backgroundColor: (updateBondMutation.isPending || showTokenLoading || !areTokenFieldsComplete()) ? 'var(--color-medium-gray)' : 'var(--color-primary)',
                  borderColor: (updateBondMutation.isPending || showTokenLoading || !areTokenFieldsComplete()) ? 'var(--color-medium-gray)' : 'var(--color-primary)',
                  color: (updateBondMutation.isPending || showTokenLoading || !areTokenFieldsComplete()) ? 'var(--color-dark-gray)' : 'white',
                  cursor: (updateBondMutation.isPending || showTokenLoading || !areTokenFieldsComplete()) ? 'not-allowed' : 'pointer',
                  opacity: (updateBondMutation.isPending || showTokenLoading || !areTokenFieldsComplete()) ? 0.8 : 1,
                }}
                onMouseEnter={(e) => {
                  if (updateBondMutation.isPending || showTokenLoading || !areTokenFieldsComplete()) return;
                  e.target.style.backgroundColor = "var(--color-white)";
                  e.target.style.color = "var(--color-primary)";
                  e.target.style.borderColor = "var(--color-primary)";
                }}
                onMouseLeave={(e) => {
                  if (updateBondMutation.isPending || showTokenLoading || !areTokenFieldsComplete()) return;
                  e.target.style.backgroundColor = "var(--color-primary)";
                  e.target.style.color = "white";
                  e.target.style.borderColor = "var(--color-primary)";
                }}
              >
                {updateBondMutation.isPending
                  ? "Saving token details..."
                  : "Continue"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );

  const renderStep5 = () => {
    const scriplessFlagValue = watch("scriplessFlag");
    const locationCodeValue = watch("locationCode");
    const confirmSubmission = watch("confirmSubmission");

    return (
      <div className="max-w-4xl mx-auto">
        <div className="card">
          <div className="card-header">
            <h2 className="mb-4 text-3xl font-bold text-secondary-900">
              Review & Submit
            </h2>
            <p className="text-lg text-secondary-600">
              Please carefully review all information before submitting for
              approval
            </p>
          </div>

          <div className="card-body">
            <div className="space-y-6">
              {/* Summary Cards */}
              <div className="p-6 bg-white border rounded-lg border-light-gray">
                <h3 className="mb-4 text-lg font-semibold text-secondary-900">
                  Bond Summary
                </h3>
                <div className="grid grid-cols-1 gap-4 text-sm md:grid-cols-2">
                  <div>
                    <span className="text-secondary-600">Bond Name:</span>
                    <span className="ml-2 font-medium">
                      {watch("bondName")}
                    </span>
                  </div>
                  <div>
                    <span className="text-secondary-600">Location Code:</span>
                    <span className="ml-2 font-medium">
                      {locationCodeValue || "—"}
                    </span>
                  </div>
                  <div>
                    <span className="text-secondary-600">Bond Type:</span>
                    <span className="ml-2 font-medium">
                      {selectedBondType === "fixed_notes"
                        ? "Conventional"
                        : "Sukuk"}
                    </span>
                  </div>
                  <div>
                    <span className="text-secondary-600">ESG Bond:</span>
                    <span className="ml-2 font-medium">
                      {(() => {
                        const esg = watch("isESGBond");
                        return esg === true || esg === "true" ? "Yes" : esg === false || esg === "false" ? "No" : "—";
                      })()}
                    </span>
                  </div>
                  <div>
                    <span className="text-secondary-600">
                      Scripless Issuance:
                    </span>
                    <span className="ml-2 font-medium">
                      {scriplessFlagValue === undefined
                        ? "—"
                        : scriplessFlagValue
                          ? "Yes"
                          : "No"}
                    </span>
                  </div>
                  <div>
                    <span className="text-secondary-600">Issue Amount:</span>
                    <span className="ml-2 font-medium">
                      MYR {watch("issueAmount")?.toLocaleString()}
                    </span>
                  </div>
                  <div>
                    <span className="text-secondary-600">Issue Date:</span>
                    <span className="ml-2 font-medium">
                      {watch("issueDate")}
                    </span>
                  </div>
                  <div>
                    <span className="text-secondary-600">Maturity Date:</span>
                    <span className="ml-2 font-medium">
                      {watch("maturityDate")}
                    </span>
                  </div>
                  <div>
                    <span className="text-secondary-600">Rate:</span>
                    <span className="ml-2 font-medium">
                      {selectedBondType === "fixed_notes"
                        ? watch("couponRate")
                        : watch("profitRate")}
                      %
                    </span>
                  </div>
                  <div>
                    <span className="text-secondary-600">Frequency:</span>
                    <span className="ml-2 font-medium">
                      {watch("couponFrequency")}
                    </span>
                  </div>
                </div>
              </div>

              {/* Uploaded Documents Summary */}
              <div className="p-6 bg-white border rounded-lg border-light-gray">
                <h3 className="mb-4 text-lg font-semibold text-secondary-900">
                  Uploaded Documents
                </h3>
                <div className="space-y-3">
                  {/* Required Documents */}
                  <div>
                    <h4 className="mb-2 text-sm font-medium text-dark-green">
                      Required Documents
                    </h4>
                    <div className="space-y-2 text-sm">
                      {selectedBondType === "fixed_notes" && (
                        <>
                          <div className="flex items-center justify-between">
                            <span className="text-secondary-600">
                              Risk Assessment Report:
                            </span>
                            <span
                              className={
                                uploadedFiles.riskAssessment
                                  ? "text-green-600"
                                  : "text-red-600"
                              }
                            >
                              {uploadedFiles.riskAssessment
                                ? "✓ Uploaded"
                                : "✗ Missing"}
                            </span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-secondary-600">
                              Legal Documents:
                            </span>
                            <span
                              className={
                                uploadedFiles.legalDocuments
                                  ? "text-green-600"
                                  : "text-red-600"
                              }
                            >
                              {uploadedFiles.legalDocuments
                                ? "✓ Uploaded"
                                : "✗ Missing"}
                            </span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-secondary-600">
                              Covenants Documents:
                            </span>
                            <span
                              className={
                                uploadedFiles.covenantsDocuments
                                  ? "text-green-600"
                                  : "text-red-600"
                              }
                            >
                              {uploadedFiles.covenantsDocuments
                                ? "✓ Uploaded"
                                : "✗ Missing"}
                            </span>
                          </div>
                          {watch("isESGBond") === true && (
                            <div className="flex items-center justify-between">
                              <span className="text-secondary-600">
                                ESG Certification:
                              </span>
                              <span
                                className={
                                  uploadedFiles.esgCertification
                                    ? "text-green-600"
                                    : "text-red-600"
                                }
                              >
                                {uploadedFiles.esgCertification
                                  ? "✓ Uploaded"
                                  : "✗ Missing"}
                              </span>
                            </div>
                          )}
                        </>
                      )}
                      {selectedBondType === "sukuk" && (
                        <>
                          <div className="flex items-center justify-between">
                            <span className="text-secondary-600">
                              Asset Valuation Report:
                            </span>
                            <span
                              className={
                                uploadedFiles.valuationReport
                                  ? "text-green-600"
                                  : "text-red-600"
                              }
                            >
                              {uploadedFiles.valuationReport
                                ? "✓ Uploaded"
                                : "✗ Missing"}
                            </span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-secondary-600">
                              Legal Documents:
                            </span>
                            <span
                              className={
                                uploadedFiles.legalDocuments
                                  ? "text-green-600"
                                  : "text-red-600"
                              }
                            >
                              {uploadedFiles.legalDocuments
                                ? "✓ Uploaded"
                                : "✗ Missing"}
                            </span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-secondary-600">
                              Covenants Documents:
                            </span>
                            <span
                              className={
                                uploadedFiles.covenantsDocuments
                                  ? "text-green-600"
                                  : "text-red-600"
                              }
                            >
                              {uploadedFiles.covenantsDocuments
                                ? "✓ Uploaded"
                                : "✗ Missing"}
                            </span>
                          </div>
                          {watch("isESGBond") === true && (
                            <div className="flex items-center justify-between">
                              <span className="text-secondary-600">
                                ESG Certification:
                              </span>
                              <span
                                className={
                                  uploadedFiles.esgCertification
                                    ? "text-green-600"
                                    : "text-red-600"
                                }
                              >
                                {uploadedFiles.esgCertification
                                  ? "✓ Uploaded"
                                  : "✗ Missing"}
                              </span>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>

                  {/* Optional Documents */}
                  {selectedBondType === "sukuk" && (
                    <div>
                      <h4 className="mb-2 text-sm font-medium text-dark-green">
                        Optional Documents
                      </h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center justify-between">
                          <span className="text-secondary-600">
                            Shariah Compliance Certificate:
                          </span>
                          <span
                            className={
                              uploadedFiles.shariahCertificate
                                ? "text-green-600"
                                : "text-gray-500"
                            }
                          >
                            {uploadedFiles.shariahCertificate
                              ? "✓ Uploaded"
                              : "Not provided"}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-secondary-600">
                            Securities Lodgement Form:
                          </span>
                          <span
                            className={
                              uploadedFiles.securitiesLodgementForm
                                ? "text-green-600"
                                : "text-gray-500"
                            }
                          >
                            {uploadedFiles.securitiesLodgementForm
                              ? "✓ Uploaded"
                              : "Not provided"}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-secondary-600">
                            Global Certificate:
                          </span>
                          <span
                            className={
                              uploadedFiles.globalCertificate
                                ? "text-green-600"
                                : "text-gray-500"
                            }
                          >
                            {uploadedFiles.globalCertificate
                              ? "✓ Uploaded"
                              : "Not provided"}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-secondary-600">
                            Debt Facility / Securities Information Form:
                          </span>
                          <span
                            className={
                              uploadedFiles.debtFacilityForm
                                ? "text-green-600"
                                : "text-gray-500"
                            }
                          >
                            {uploadedFiles.debtFacilityForm
                              ? "✓ Uploaded"
                              : "Not provided"}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Confirmation */}
              <div>
                <div className="flex items-start space-x-3">
                  <input
                    type="checkbox"
                    id="confirm-submission"
                    {...register("confirmSubmission")}
                    className="mt-1"
                  />
                  <label
                    htmlFor="confirm-submission"
                    className="text-sm text-dark-green"
                  >
                    I confirm that all the above information is accurate and
                    complete. I understand that once submitted, this bond
                    application will be sent to the Arranger for review and
                    approval. I agree to the terms and conditions of the
                    platform.
                  </label>
                </div>
              </div>

              <div className="flex justify-between">
                <button
                  type="button"
                  onClick={() => setCurrentStep(4)}
                  className="flex items-center px-6 py-3 transition-colors rounded focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
                  style={{
                    fontFamily: '"Roboto", sans-serif',
                    fontWeight: 700,
                    backgroundColor: "var(--color-gray)",
                    color: "var(--color-dark-gray)",
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = "var(--color-medium-gray)";
                    e.target.style.color = "var(--color-dark-gray)";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = "var(--color-gray)";
                    e.target.style.color = "var(--color-dark-gray)";
                  }}
                >
                  <ChevronLeft className="w-4 h-4 mr-2" />
                  Back
                </button>

                <div className="flex space-x-4">
                  <button
                    type="button"
                    className="flex items-center px-6 py-3 transition-colors rounded focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
                    onClick={() => navigate("/issuer")}
                    style={{
                      fontFamily: '"Roboto", sans-serif',
                      fontWeight: 700,
                      backgroundColor: "var(--color-gray)",
                      color: "var(--color-dark-gray)",
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor =
                        "var(--color-medium-gray)";
                      e.target.style.color = "var(--color-dark-gray)";
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = "var(--color-gray)";
                      e.target.style.color = "var(--color-dark-gray)";
                    }}
                  >
                    Save as Draft
                  </button>
                  <button
                    type="button"
                    onClick={handleFinalSubmit}
                    disabled={submitBondMutation.isPending || !confirmSubmission}
                    className="flex items-center px-6 py-3 transition-colors border rounded focus:outline-none focus:ring-2 focus:ring-offset-2"
                    style={{
                      fontFamily: '"Roboto", sans-serif',
                      fontWeight: 700,
                      backgroundColor: (submitBondMutation.isPending || !confirmSubmission) ? 'var(--color-medium-gray)' : 'var(--color-primary)',
                      borderColor: (submitBondMutation.isPending || !confirmSubmission) ? 'var(--color-medium-gray)' : 'var(--color-primary)',
                      color: (submitBondMutation.isPending || !confirmSubmission) ? 'var(--color-dark-gray)' : 'white',
                      cursor: (submitBondMutation.isPending || !confirmSubmission) ? 'not-allowed' : 'pointer',
                      opacity: (submitBondMutation.isPending || !confirmSubmission) ? 0.8 : 1,
                    }}
                    onMouseEnter={(e) => {
                      if (submitBondMutation.isPending || !confirmSubmission) return;
                      e.target.style.backgroundColor = "var(--color-white)";
                      e.target.style.color = "var(--color-primary)";
                      e.target.style.borderColor = "var(--color-primary)";
                    }}
                    onMouseLeave={(e) => {
                      if (submitBondMutation.isPending || !confirmSubmission) return;
                      e.target.style.backgroundColor = "var(--color-primary)";
                      e.target.style.color = "white";
                      e.target.style.borderColor = "var(--color-primary)";
                    }}
                  >
                    {submitBondMutation.isPending ? (
                      <LoadingSpinner size="sm" />
                    ) : (
                      <>
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Submit for Approval
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="px-4 py-8 mx-auto max-w-7xl sm:px-6 lg:px-8">
        {/* Page Header */}
        <div className="flex flex-col gap-4 mb-12 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Create Bond Application</h1>
            <p className="mt-1 text-secondary-600">
              Set up your bond issuance with all required details and
              documentation
            </p>
          </div>
        </div>
        {!showSubmissionSuccess && currentStep !== 1 && renderStepIndicator()}

        <div className="min-h-96">
          {currentStep === 1 && renderStep1()}
          {currentStep === 2 && renderStep2()}
          {currentStep === 3 && renderStep3()}
          {currentStep === 4 && renderStep4()}
          {currentStep === 5 && !showSubmissionSuccess && renderStep5()}
          {showSubmissionSuccess && renderSubmissionSuccess()}
        </div>
      </div>

      {/* Token Details/Upload Overlay (creation deferred to submission) */}
      {(showTokenLoading ||
        uploadingDocuments ||
        (updateBondMutation.isPending && currentStep === 4) ||
        isTokenCreationInProgress || submitBondMutation.isPending) && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="w-full max-w-sm p-6 mx-4 text-center bg-white rounded-lg">
              <div className="flex justify-center mb-4">
                <LoadingSpinner />
              </div>
              {isTokenCreationInProgress ? (
                <>
                  <h3 className="text-lg font-medium text-gray-900">Creating On-Chain Token...</h3>
                  <p className="mt-2 text-sm text-gray-600">Your bond was submitted. Waiting for token deployment to finalize.</p>
                  <p className="mt-2 text-xs text-gray-500">This may take up to a minute depending on network conditions.</p>
                </>
              ) : submitBondMutation.isPending ? (
                <>
                  <h3 className="text-lg font-medium text-gray-900">Submitting Bond...</h3>
                  <p className="mt-2 text-sm text-gray-600">Please wait while we submit your bond for approval.</p>
                </>
              ) : uploadingDocuments ? (
                <>
                  <h3 className="text-lg font-medium text-gray-900">
                    Uploading Documents
                  </h3>
                  <p className="mt-2 text-sm text-gray-600">
                    Please wait while we upload your supporting documents.
                  </p>
                  <div className="w-full h-2 mt-4 bg-gray-200 rounded-full">
                    <div
                      className="h-2 transition-all rounded-full bg-primary-500"
                      style={{ width: `${documentUploadProgress}%` }}
                    ></div>
                  </div>
                  <p className="mt-2 text-xs text-gray-500">
                    {documentUploadProgress}%
                  </p>
                </>
              ) : (
                <>
                  <h3 className="text-lg font-medium text-gray-900">
                    Saving Token Details
                  </h3>
                  <p className="mt-2 text-sm text-gray-600">
                    Token will be created after you submit the bond for approval.
                  </p>
                </>
              )}
            </div>
          </div>
        )}
    </div>
  );
};

export default CreateBond;
